import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { BranchProvider } from './contexts/BranchContext';
import { ApprovalNotificationProvider } from './contexts/ApprovalNotificationContext';
import { CallManagementProvider } from './contexts/CallManagementContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import VerifyEmail from './components/VerifyEmail';
import ResendVerification from './components/ResendVerification';
import Dashboard from './components/Dashboard';
import Leads from './components/Leads';
import LeadsPipeline from './components/LeadsPipeline';
import LeadForm from './components/LeadForm';
import LeadDetail from './components/LeadDetail';
import Settings from './components/Settings';
import ProfileSettings from './components/ProfileSettings';
import FormTemplatesPage from './components/FormTemplatesPage';
import ServicesManager from './components/ServicesManager';
import ServiceDetail from './components/ServiceDetail';
import EmailTemplates from './pages/Settings/EmailTemplates';
import Forms from './components/Forms';
import PublicForm from './components/PublicForm';
import FloatingCallIcon from './components/FloatingCallIcon';
import EndCallModal from './components/EndCallModal';
import Calls from './components/Calls';
import { useCallManagement } from './contexts/CallManagementContext';
import Email from './components/Email';
import Documents from './components/Documents';
import DocumentSubmissionForm from './components/DocumentSubmissionForm';
import ReferenceList from './components/ReferenceList';
import ReferenceDetail from './components/ReferenceDetail';
import TaskList from './components/TaskList';
import TaskDetail from './components/TaskDetail';
import NotesList from './components/NotesList';
import Timeline from './components/Timeline';
import UniversityPortal from './components/UniversityPortal';
import Automation from './components/Automation';
import BranchManagement from './components/BranchManagement';
import AcceptInvitation from './components/AcceptInvitation';
import GlobalApprovalModal from './components/GlobalApprovalModal';
import NotificationsPage from './pages/NotificationsPage';
import NotificationSettingsPage from './pages/NotificationSettingsPage';
import { NotificationProvider } from './contexts/NotificationContext';
import { callsAPI } from './services/callApi';

// Inner component to access call management context
const AppContent: React.FC = () => {
  const { liveCall, showEndCallModal, setShowEndCallModal, endCallDurationSnapshot, setEndCallDurationSnapshot, endCall, cancelCall, rescheduleCall } = useCallManagement();
  const [callDurationFallback, setCallDurationFallback] = React.useState(0);

  // Fallback duration if snapshot not set (e.g. modal opened from elsewhere)
  React.useEffect(() => {
    if (!liveCall || !showEndCallModal || endCallDurationSnapshot !== null) return;
    if (!liveCall.actualStartTime) return;
    const startTime = new Date(liveCall.actualStartTime);
    const now = new Date();
    setCallDurationFallback(Math.floor((now.getTime() - startTime.getTime()) / 1000));
  }, [liveCall, showEndCallModal, endCallDurationSnapshot]);

  const endCallModalDuration = endCallDurationSnapshot ?? callDurationFallback;

  return (
    <Router>
      <div className="App">
        <GlobalApprovalModal />
        <FloatingCallIcon />
        
        {/* End Call Modal */}
        {liveCall && showEndCallModal && (
          <EndCallModal
            call={liveCall}
            duration={endCallModalDuration}
            onEndCall={endCall}
            onReschedule={rescheduleCall}
            onCancelCall={cancelCall}
            onClose={() => {
              setShowEndCallModal(false);
              setEndCallDurationSnapshot(null);
            }}
          />
        )}
                <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/resend-verification" element={<ResendVerification />} />
          <Route path="/accept-invitation" element={<AcceptInvitation />} />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/leads" 
            element={
              <ProtectedRoute>
                <Leads />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/leads/pipeline" 
            element={
              <ProtectedRoute>
                <LeadsPipeline />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/leads/new" 
            element={
              <ProtectedRoute>
                <LeadForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/leads/edit/:id" 
            element={
              <ProtectedRoute>
                <LeadForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/leads/:id" 
            element={
              <ProtectedRoute>
                <LeadDetail />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/profile-settings" 
            element={
              <ProtectedRoute>
                <ProfileSettings />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/settings" 
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/services" 
            element={
              <ProtectedRoute>
                <ServicesManager />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/services/:id" 
            element={
              <ProtectedRoute>
                <ServiceDetail />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/email-templates" 
            element={
              <ProtectedRoute>
                <EmailTemplates />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/form-templates" 
            element={
              <ProtectedRoute>
                <FormTemplatesPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/forms" 
            element={
              <ProtectedRoute>
                <Forms />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/calls" 
            element={
              <ProtectedRoute>
                <Calls />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/emails" 
            element={
              <ProtectedRoute>
                <Email />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/documents" 
            element={
              <ProtectedRoute>
                <Documents />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/references" 
            element={
              <ProtectedRoute>
                <ReferenceList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/references/:id" 
            element={
              <ProtectedRoute>
                <ReferenceDetail />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tasks" 
            element={
              <ProtectedRoute>
                <TaskList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tasks/:id" 
            element={
              <ProtectedRoute>
                <TaskDetail />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/notes" 
            element={
              <ProtectedRoute>
                <NotesList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/timeline" 
            element={
              <ProtectedRoute>
                <Timeline />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/university-portal" 
            element={
              <ProtectedRoute>
                <UniversityPortal />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/automation" 
            element={
              <ProtectedRoute>
                <Automation />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/branches" 
            element={
              <ProtectedRoute>
                <BranchManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/public-form/:token" 
            element={<PublicForm />} 
          />
          <Route 
            path="/documents/submit/:token" 
            element={<DocumentSubmissionForm />} 
          />
          <Route 
            path="/notifications" 
            element={
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/notifications/settings" 
            element={
              <ProtectedRoute>
                <NotificationSettingsPage />
              </ProtectedRoute>
            } 
          />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </div>
            </Router>
  );
};

function App() {
  // Global call management handlers that make actual API calls
  const handleEndCall = async (callId: number, summary: string, callPickedUp: boolean, durationSeconds?: number) => {
    try {
      // Validate summary before sending
      if (!summary || !summary.trim()) {
        throw new Error('Summary is required when ending a call');
      }
      
      await callsAPI.endCall(callId, { summary: summary.trim(), callPickedUp, durationSeconds });
    } catch (error: any) {
      console.error('Error ending call:', error);
      // Log more details if it's an axios error
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      throw error;
    }
  };

  const handleCancelCall = async (callId: number, reason: string) => {
    try {
      await callsAPI.handleNotPickedUp(callId, { reason });
    } catch (error) {
      console.error('Error canceling call:', error);
      throw error;
    }
  };

  const handleRescheduleCall = async (callId: number, newDate: string, reason: string) => {
    try {
      await callsAPI.rescheduleCall(callId, { newDate, reason });
    } catch (error) {
      console.error('Error rescheduling call:', error);
      throw error;
    }
  };

  return (
    <AuthProvider>
      <BranchProvider>
        <NotificationProvider>
          <ApprovalNotificationProvider>
            <CallManagementProvider
              onEndCall={handleEndCall}
              onCancelCall={handleCancelCall}
              onRescheduleCall={handleRescheduleCall}
            >
              <AppContent />
            </CallManagementProvider>
          </ApprovalNotificationProvider>
        </NotificationProvider>
      </BranchProvider>
    </AuthProvider>
  );
}

export default App;
